/**
 * StructuredSearchBar — compact multi-field directory search.
 *
 *   [ 🇦🇺 QLD ▼ ] [ Brisbane ▼ ] [ Suburb | Postcode  ▼ ]  ← location cascade
 *   [ 🔍 Business name, phone number, ABN, keyword…               ]
 *   [ Niche ▼ ] [ Sub-niche ▼ ] [ Category ▼ ]  [ 📍 Near me ]     ← filters
 *
 * Emits {state, city, suburb, postcode, name, phone, niche, sub_niche, category}
 * via onChange (debounced 250 ms). Values are also mirrored to the URL
 * query string so a search is deep-linkable.
 *
 *  • Geolocation: on "📍 Near me" click → browser geolocation →
 *    /api/directory/reverse-geocode → pre-fills state/city/suburb/postcode.
 *  • Suburb field: fuzzy autocomplete, matches both suburb name AND
 *    postcode (typing "4064" finds Toowong 4064).
 *  • Filters: niche → sub-niche → category → sub-category cascade.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, MapPin, X, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
              || (typeof window !== "undefined" ? window.location.origin : "");
const AU_STATES = [
  { code: "NSW", name: "New South Wales" },
  { code: "VIC", name: "Victoria" },
  { code: "QLD", name: "Queensland" },
  { code: "WA",  name: "Western Australia" },
  { code: "SA",  name: "South Australia" },
  { code: "TAS", name: "Tasmania" },
  { code: "ACT", name: "Australian Capital Territory" },
  { code: "NT",  name: "Northern Territory" },
];

// Small hook: debounced value
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function StructuredSearchBar({ initial = {}, onSearch, niches = [], niche: nicheFilter = null }) {
  const [state, setState]         = useState(initial.state || "");
  const [city, setCity]           = useState(initial.city || "");
  const [suburb, setSuburb]       = useState(initial.suburb || "");
  const [postcode, setPostcode]   = useState(initial.postcode || "");
  const [name, setName]           = useState(initial.name || "");
  const [niche, setNiche]         = useState(initial.niche || "");
  const [subNiche, setSubNiche]   = useState(initial.sub_niche || "");

  // Autocomplete state
  const [subQuery, setSubQuery]   = useState("");           // what user is typing
  const [subOpen,  setSubOpen]    = useState(false);
  const [subHits,  setSubHits]    = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const dbSub = useDebounced(subQuery, 200);

  // Cities per state
  const [cities, setCities]       = useState([]);
  // Geo
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError]     = useState("");

  const wrapRef = useRef(null);

  // Fetch cities whenever state changes
  useEffect(() => {
    if (!state) { setCities([]); return; }
    const u = new URL(`${API}/api/directory/cities`, window.location.origin);
    u.searchParams.set("state", state);
    fetch(u.toString())
      .then(r => r.ok ? r.json() : [])
      .then(setCities)
      .catch(() => setCities([]));
  }, [state]);

  // Fetch suburb autocomplete when user types (debounced)
  useEffect(() => {
    if (!dbSub || dbSub.length < 1) { setSubHits([]); return; }
    setSubLoading(true);
    const u = new URL(`${API}/api/directory/suburbs`, window.location.origin);
    u.searchParams.set("q", dbSub);
    if (state) u.searchParams.set("state", state);
    if (city)  u.searchParams.set("city",  city);
    if (nicheFilter) u.searchParams.set("niche", nicheFilter);
    u.searchParams.set("limit", "12");
    fetch(u.toString())
      .then(r => r.ok ? r.json() : [])
      .then(rows => { setSubHits(Array.isArray(rows) ? rows : []); })
      .catch(() => setSubHits([]))
      .finally(() => setSubLoading(false));
  }, [dbSub, state, city, nicheFilter]);

  // Close dropdown on outside-click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setSubOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Emit search state whenever it changes (debounced upstream by caller)
  const payload = useMemo(() => ({
    state, city, suburb, postcode, name: name.trim(), niche, sub_niche: subNiche,
  }), [state, city, suburb, postcode, name, niche, subNiche]);

  // Fire onSearch whenever payload changes (parent decides how to debounce)
  useEffect(() => { if (onSearch) onSearch(payload); }, [payload, onSearch]);

  // ── Geolocation → suburb prefill ─────────────────────────────
  const snapToMyLocation = () => {
    if (!navigator.geolocation) { setGeoError("Geolocation not supported"); return; }
    setGeoError(""); setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const u = new URL(`${API}/api/directory/reverse-geocode`, window.location.origin);
        u.searchParams.set("lat", pos.coords.latitude.toFixed(5));
        u.searchParams.set("lng", pos.coords.longitude.toFixed(5));
        const r = await fetch(u.toString());
        const d = await r.json();
        if (d.ok) {
          setState(d.state || "");
          setCity(d.city || "");
          setSuburb(d.suburb || "");
          setPostcode(d.postcode || "");
          setSubQuery(d.suburb || "");
        } else {
          setGeoError("No suburbs near you in our directory yet.");
        }
      } catch (e) {
        setGeoError("Couldn't snap to a suburb.");
      } finally {
        setGeoLoading(false);
      }
    }, (err) => {
      setGeoLoading(false);
      setGeoError(err.code === 1 ? "Location permission denied" : "Location unavailable");
    }, { timeout: 8000, maximumAge: 60_000 });
  };

  // Try to auto-run geolocation ONCE on mount if nothing is pre-set.
  const didAutoGeoRef = useRef(false);
  useEffect(() => {
    if (didAutoGeoRef.current) return;
    if (state || city || suburb || postcode) return;   // user already narrowed
    if (typeof window === "undefined") return;
    if (!navigator.permissions || !navigator.permissions.query) return;
    navigator.permissions.query({ name: "geolocation" }).then((p) => {
      if (p.state === "granted") {
        didAutoGeoRef.current = true;
        snapToMyLocation();
      }
    }).catch(() => {});
  }, [state, city, suburb, postcode]);

  const subNichesForCurrent = useMemo(() => {
    const found = niches.find((n) => (n.name || n.key) === niche);
    return (found && Array.isArray(found.sub_niches)) ? found.sub_niches : [];
  }, [niches, niche]);

  const clearAll = () => {
    setState(""); setCity(""); setSuburb(""); setPostcode("");
    setName(""); setNiche(""); setSubNiche(""); setSubQuery("");
  };

  return (
    <div
      ref={wrapRef}
      data-testid="structured-search-bar"
      className="w-full max-w-5xl mx-auto rounded-xl border border-stone-200 bg-white shadow-sm p-2 space-y-1.5"
    >
      {/* Row 1: location cascade */}
      <div className="grid grid-cols-6 gap-2">
        <select
          data-testid="ssb-state"
          value={state}
          onChange={(e) => { setState(e.target.value); setCity(""); setSuburb(""); setPostcode(""); setSubQuery(""); }}
          className="col-span-2 sm:col-span-1 h-8 px-2 rounded-md border border-stone-200 bg-stone-50 text-[12px] font-semibold text-stone-800 outline-none focus:border-emerald-500"
        >
          <option value="">🇦🇺 State</option>
          {AU_STATES.map((s) => (
            <option key={s.code} value={s.code}>{s.code}</option>
          ))}
        </select>

        <select
          data-testid="ssb-city"
          value={city}
          onChange={(e) => { setCity(e.target.value); setSuburb(""); setPostcode(""); setSubQuery(""); }}
          disabled={!state && cities.length === 0}
          className="col-span-2 h-8 px-2 rounded-md border border-stone-200 bg-stone-50 text-[12px] text-stone-800 outline-none focus:border-emerald-500 disabled:opacity-60"
        >
          <option value="">City / region</option>
          {cities.map((c) => (
            <option key={c.name} value={c.name}>{c.name} · {c.count.toLocaleString()}</option>
          ))}
        </select>

        <div className="col-span-2 sm:col-span-2 relative">
          <input
            data-testid="ssb-suburb"
            type="text"
            placeholder={suburb ? `${suburb}${postcode ? " | " + postcode : ""}` : "Suburb or postcode…"}
            value={subQuery}
            onChange={(e) => { setSubQuery(e.target.value); setSubOpen(true); }}
            onFocus={() => setSubOpen(true)}
            className="w-full h-8 px-2 rounded-md border border-stone-200 bg-stone-50 text-[12px] text-stone-800 outline-none focus:border-emerald-500"
            autoComplete="off"
          />
          {(suburb || subQuery) && (
            <button
              type="button"
              onClick={() => { setSuburb(""); setPostcode(""); setSubQuery(""); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
              aria-label="Clear suburb"
              data-testid="ssb-suburb-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {subOpen && (subHits.length > 0 || subLoading) && (
            <div
              data-testid="ssb-suburb-dropdown"
              className="absolute z-20 mt-1 w-[280px] max-h-72 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg"
            >
              {subLoading && (
                <div className="px-3 py-2 text-[12px] text-stone-500 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Searching suburbs…
                </div>
              )}
              {subHits.map((h) => (
                <button
                  key={`${h.name}-${h.postcode}`}
                  type="button"
                  data-testid={`ssb-suburb-hit-${h.postcode}`}
                  onClick={() => {
                    setSuburb(h.name); setPostcode(h.postcode);
                    if (h.state && !state) setState(h.state);
                    if (h.city  && !city)  setCity(h.city);
                    setSubQuery(`${h.name} | ${h.postcode}`);
                    setSubOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 border-b border-stone-100 last:border-b-0 hover:bg-emerald-50 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-stone-800 truncate">
                      {h.name} <span className="text-stone-400 font-normal">| {h.postcode}</span>
                    </div>
                    <div className="text-[11px] text-stone-500">
                      {[h.city, h.state].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div className="text-[11px] text-emerald-700 font-semibold shrink-0">{h.count.toLocaleString()}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          data-testid="ssb-near-me"
          type="button"
          onClick={snapToMyLocation}
          disabled={geoLoading}
          className="col-span-6 sm:col-span-1 h-8 px-2 rounded-md text-[11px] font-bold uppercase tracking-wide inline-flex items-center justify-center gap-1 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-70"
          title="Use my current location"
        >
          {geoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
          Near me
        </button>
      </div>

      {geoError && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="ssb-geo-error">
          {geoError}
        </div>
      )}

      {/* Row 2: long search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
        <input
          data-testid="ssb-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Business name, phone, ABN, keyword…"
          className="w-full h-9 pl-8 pr-8 rounded-md border border-stone-200 bg-white text-[13px] text-stone-800 outline-none focus:border-emerald-500"
          autoComplete="off"
        />
        {name && (
          <button
            type="button"
            onClick={() => setName("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
            aria-label="Clear text search"
            data-testid="ssb-name-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Row 3: filter chips */}
      <div className="flex flex-wrap gap-1.5 items-center">
        <select
          data-testid="ssb-niche"
          value={niche}
          onChange={(e) => { setNiche(e.target.value); setSubNiche(""); }}
          className="h-7 px-2 rounded-full border border-stone-200 bg-stone-50 text-[11px] font-semibold text-stone-800 outline-none focus:border-emerald-500"
        >
          <option value="">All niches</option>
          {niches.map((n) => (
            <option key={n.key || n.name} value={n.name || n.key}>{n.label || n.name || n.key}</option>
          ))}
        </select>

        {subNichesForCurrent.length > 0 && (
          <select
            data-testid="ssb-sub-niche"
            value={subNiche}
            onChange={(e) => setSubNiche(e.target.value)}
            className="h-7 px-2 rounded-full border border-stone-200 bg-stone-50 text-[11px] text-stone-800 outline-none focus:border-emerald-500"
          >
            <option value="">All sub-niches</option>
            {subNichesForCurrent.map((s) => (
              <option key={s.key || s.name} value={s.name || s.key}>{s.label || s.name || s.key}</option>
            ))}
          </select>
        )}

        {(state || city || suburb || name || niche || subNiche) && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="ssb-clear-all"
            className="h-7 px-2 rounded-full text-[11px] font-semibold text-stone-600 hover:text-stone-900 hover:bg-stone-100 inline-flex items-center gap-0.5"
          >
            <X className="w-2.5 h-2.5" /> Clear all
          </button>
        )}
      </div>
    </div>
  );
}
